import { endOfDay, endOfWeek, startOfDay } from 'date-fns';
import { useState } from 'react';
import { FiX } from 'react-icons/fi';

import { CoachButton } from '@/app/components/CoachButton';
import { Dialog } from '@/app/components/Dialog';
import type { TimelyAnalysis } from '@trainme/db/client';
import type { Week } from './InfiniteCalendar';
import { zoneColors } from '@/app/theme/colors';

interface WeeklyScoreBarProps {
  week: Week;
  analysis: TimelyAnalysis;
}

export const WeeklyScoreBar = ({ week, analysis }: WeeklyScoreBarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const scoreColorMap = new Map<number, string>([
    [0.6, zoneColors['zone-5']], // Red for scores < 60%
    [0.7, zoneColors['zone-4']], // Orange for scores 60-70%
    [0.8, zoneColors['zone-3']], // Yellow for scores 70-80%
    [1.0, zoneColors['zone-2']], // Green for scores >= 80%
  ]);

  const getColorForScore = (score: number) => {
    for (const [threshold, color] of scoreColorMap) {
      if (score < threshold) {
        return color;
      }
    }
    return zoneColors['zone-2']; // Default to green for scores >= 100%
  };

  const today = startOfDay(new Date());
  const weekEnd = endOfDay(endOfWeek(week.start, { weekStartsOn: 1 }));

  if (
    analysis.trainmeScore === null ||
    analysis.trainmeScore === undefined ||
    weekEnd >= today
  ) {
    return null;
  }

  return (
    <>
      <div
        onClick={() => setIsOpen(true)}
        className='bg-gray-200 dark:bg-gray-800 w-14 rounded-sm overflow-hidden h-6 cursor-pointer flex-shrink-0'
      >
        <div
          className='flex justify-center items-center h-full'
          style={{
            width: `${(analysis.trainmeScore || 0) * 100}%`,
            background: getColorForScore(analysis.trainmeScore || 0),
            height: '100%',
            minWidth: analysis.trainmeScore ? '20px' : '0',
          }}
        >
          <div className='font-bold text-gray-100 dark:text-gray-900 text-sm'>
            {Math.round(analysis.trainmeScore * 100)}
          </div>
        </div>
      </div>
      <Dialog
        show={isOpen}
        onClose={() => setIsOpen(false)}
        className='w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] xs:w-[calc(100vw-2rem)] xs:max-w-[calc(100vw-2rem)] lg:w-[min(60vw,700px)] lg:max-w-[min(60vw,700px)]'
      >
        <Dialog.Header className='flex items-center justify-between gap-3'>
          <span className='font-semibold'>Weekly Analysis</span>
          <div className='flex items-center gap-3 ml-auto'>
            <CoachButton
              messageId={analysis.coachMessageId}
              className='btn btn-secondary rounded-full py-1 px-3 relative text-xs'
            />
            <button
              onClick={() => setIsOpen(false)}
              className='p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors text-gray-500 dark:text-gray-400'
              aria-label='Close'
            >
              <FiX size={18} />
            </button>
          </div>
        </Dialog.Header>
        <Dialog.Body>
          <div className='prose dark:prose-invert whitespace-pre-wrap max-w-none'>
            {analysis.coachSay}
          </div>
        </Dialog.Body>
      </Dialog>
    </>
  );
};
